/**
 * Centralised React Query key factory.
 *
 * One typo-proof source of truth for every cache key, so list/detail reads,
 * their refetches, and the post-mutation invalidate/remove calls can never
 * drift apart (a stale inline `['apps', id]` somewhere = a ghost row or a
 * cache miss). Mirror the existing idiom in useExecutions / useIntegrations
 * (queryKey is an array) — these helpers just BUILD that array consistently.
 *
 * Shape: `queryKeys.<domain>.<scope>(...args)` → a stable array.
 *   queryKeys.apps.all                       → ['apps']
 *   queryKeys.apps.list(projectId)           → ['apps', 'list', projectId]
 *   queryKeys.apps.detail(instanceId)        → ['apps', 'detail', instanceId]
 *   queryKeys.agents.all                     → ['agents']
 *   queryKeys.agents.list(projectId)         → ['agents', 'list', projectId]
 *   queryKeys.agents.detail(projectId, slug) → ['agents', 'detail', projectId, slug]
 *
 * The `all` roots let a caller invalidate an entire domain in one shot
 * (React Query prefix-matches), while `list` / `detail` scope precisely.
 */
export const queryKeys = {
  projects: {
    all: ['projects'],
    list: () => ['projects', 'list'],
  },
  apps: {
    all: ['apps'],
    list: (projectId) => ['apps', 'list', projectId],
    detail: (instanceId) => ['apps', 'detail', instanceId],
  },
  agents: {
    all: ['agents'],
    list: (projectId) => ['agents', 'list', projectId],
    // Agent detail is keyed by (projectId, workflowType-slug) because that's
    // the live access pattern: the detail page reads
    // GET /projects/:projectId/workflows/:workflowType (NOT a uuid route).
    detail: (projectId, workflowType) => ['agents', 'detail', projectId, workflowType],
  },
};

export default queryKeys;
